<template>
	<SimulatorSheet :stepNum="stepNum" :stepName="stepName">
		<template #headerBody>
			<div>
				<div class="mb-20">
					<BSelector
						design="split"
						:options="simulationOptions.windDeltasCfg.options"
						v-model="values.wind.cfgMode.deltaCfgOption"
					/>
				</div>

				<div class="grid--4 mb-20">
					<BFieldBlock
						v-for="(num, index) in 3"
						:key="index"
						:label="`Ψ${index}`"
					>
						<BFieldNumber
							:disabled="values.wind.cfgMode.deltaCfgOption !== 'customized'"
							v-model="values.wind.deltas[index]"
							:min="0"
							:precision="2"
							:step="0.01"
						/>
					</BFieldBlock>
				</div>
			</div>
		</template>

		<div class="simulator__grid simulator__grid--1-2">
			<div>
				<div class="sheet__controlTitle mb-20">{{ $t("Vento") }}</div>
				<ImageWind class="sheet__image" />
			</div>
			<div>
				<div class="mb-40">
					<BToggle
						:label="$t('Definir por banda')"
						v-model="values.wind.cfgMode.isBandByBand"
					/>
				</div>

				<div class="mb-80">
					<div v-if="!values.wind.cfgMode.isBandByBand" class="grid--4">
						<BFieldBlock :label="$t('Pressão')">
							<BFieldNumber
								v-model="values.wind.general.pressure.w[0]"
								:min="0"
								suffix="kN/m2"
								:precision="2"
								:step="0.01"
							/>
						</BFieldBlock>

						<BFieldBlock :label="$t('Sucção')">
							<BFieldNumber
								v-model="values.wind.general.suction.w[0]"
								:min="0"
								suffix="kN/m2"
								:precision="2"
								:step="0.01"
							/>
						</BFieldBlock>
					</div>

					<div v-else>
						<p class="color-neutral-600 font-size-l mb-20 ml-field-label">
							{{ $t("Pressão") }}
						</p>
						<div class="mb-80">
							<div class="grid--4 mb-60">
								<BFieldBlock
									v-for="(item, idx) in values.wind.general.pressure.w"
									:key="idx"
									:label="`W${idx + 1}`"
								>
									<BFieldNumber
										v-model="values.wind.general.pressure.w[idx]"
										:min="0"
										suffix="kN/m2"
										:precision="2"
										:step="0.01"
									/>
								</BFieldBlock>
							</div>

							<div class="grid--4">
								<BFieldBlock
									v-for="(item, idx) in values.wind.general.pressure.l"
									:key="idx"
									:label="`L${idx + 1}`"
								>
									<BFieldNumber
										v-model="values.wind.general.pressure.l[idx]"
										:min="0"
										:max="
											getMaxForLengthField({
												lengthFields: values.wind.general.pressure.l,
												idx,
											})
										"
										:precision="2"
										:step="0.01"
										suffix="m"
									/>
								</BFieldBlock>
							</div>
						</div>

						<p class="color-neutral-600 font-size-l mb-20 ml-field-label">
							{{ $t("Sucção") }}
						</p>

						<div>
							<div class="grid--4 mb-60">
								<BFieldBlock
									v-for="(item, idx) in values.wind.general.suction.w"
									:key="idx"
									:label="`W${idx + 1}`"
								>
									<BFieldNumber
										v-model="values.wind.general.suction.w[idx]"
										:min="0"
										suffix="kN/m2"
										:precision="2"
										:step="0.01"
									/>
								</BFieldBlock>
							</div>

							<div class="grid--4">
								<BFieldBlock
									v-for="(item, idx) in values.wind.general.suction.l"
									:key="idx"
									:label="`L${idx + 1}`"
								>
									<BFieldNumber
										v-model="values.wind.general.suction.l[idx]"
										:min="0"
										:max="
											getMaxForLengthField({
												lengthFields: values.wind.general.suction.l,
												idx,
											})
										"
										suffix="m"
										:precision="2"
										:step="0.01"
									/>
								</BFieldBlock>
							</div>
						</div>
					</div>
				</div>

				<button
					class="sheet__bigAddBtn sheet__bigAddBtn--dark"
					@click="handleAddLocalWindBtnClick"
					v-if="!values.wind.local.use"
				>
					{{ $t("Vento local") }} ({{ $t("opcional") }})
					<BIcon name="AddAlt" size="16"></BIcon>
				</button>

				<div class="additionalFieldsCard" v-else>
					<div class="flex align-items-center justify-between mb-30">
						<p class="font-size-l color-neutral-900">
							{{ $t("Vento local") }} ({{ $t("opcional") }})
						</p>

						<div class="flex">
							<!-- <BBtn
								size="m"
								variant="text"
								shape="circle"
								color="secondary"
								@click="handleRefreshLocalWindBtnClick"
							>
								<BIcon name="Reset" size="20px" />
							</BBtn> -->
							<BBtn
								size="m"
								variant="text"
								shape="circle"
								color="secondary"
								@click="handleDeleteLocalWindBtnClick"
							>
								<BIcon name="Delete" size="20px" />
							</BBtn>
						</div>
					</div>

					<div v-if="!values.wind.cfgMode.isBandByBand" class="grid--4">
						<BFieldBlock :label="$t('Pressão')">
							<BFieldNumber
								v-model="values.wind.local.pressure.w[0]"
								suffix="kN/m2"
								:precision="2"
								:step="0.01"
							/>
						</BFieldBlock>

						<BFieldBlock :label="$t('Sucção')">
							<BFieldNumber
								v-model="values.wind.local.suction.w[0]"
								suffix="kN/m2"
								:precision="2"
								:step="0.01"
							/>
						</BFieldBlock>
					</div>

					<template v-else>
						<p class="color-neutral-600 font-size-l mb-20 ml-field-label">
							{{ $t("Pressão") }}
						</p>
						<div class="mb-80">
							<div class="grid--4 mb-60">
								<BFieldBlock
									v-for="(item, idx) in values.wind.local.pressure.w"
									:label="`W${idx + 1}`"
									:key="idx"
								>
									<BFieldNumber
										v-model="values.wind.local.pressure.w[idx]"
										:min="0"
										suffix="kN/m2"
										:precision="2"
										:step="0.01"
									/>
								</BFieldBlock>
							</div>

							<div class="grid--4">
								<BFieldBlock
									v-for="(item, idx) in values.wind.local.pressure.l"
									:label="`L${idx + 1}`"
									:key="idx"
								>
									<BFieldNumber
										v-model="values.wind.local.pressure.l[idx]"
										:min="0"
										:max="
											getMaxForLengthField({
												lengthFields: values.wind.local.pressure.l,
												idx,
											})
										"
										suffix="m"
										:precision="2"
										:step="0.01"
									/>
								</BFieldBlock>
							</div>
						</div>

						<p class="color-neutral-600 font-size-l mb-20 ml-field-label">
							{{ $t("Sucção") }}
						</p>

						<div>
							<div class="grid--4 mb-60">
								<BFieldBlock
									v-for="(item, idx) in values.wind.local.suction.w"
									:label="`W${idx + 1}`"
									:key="idx"
								>
									<BFieldNumber
										v-model="values.wind.local.suction.w[idx]"
										:min="0"
										suffix="kN/m2"
										:precision="2"
										:step="0.01"
									/>
								</BFieldBlock>
							</div>

							<div class="grid--4">
								<BFieldBlock
									v-for="(item, idx) in values.wind.local.suction.l"
									:label="`L${idx + 1}`"
									:key="idx"
								>
									<BFieldNumber
										v-model="values.wind.local.suction.l[idx]"
										:min="0"
										:max="
											getMaxForLengthField({
												lengthFields: values.wind.local.suction.l,
												idx,
											})
										"
										suffix="m"
										:precision="2"
										:step="0.01"
									/>
								</BFieldBlock>
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>

		<div class="sheet__footer">
			<BBtn
				size="l"
				variant="text"
				@click="handleRestartBtnClick"
				:disabled="isSaving"
				>{{ $t("Reiniciar") }}</BBtn
			>
			<BBtn size="l" @click="handleSaveBtnClick" :disabled="isSaving">{{
				$t("Salvar e Continuar")
			}}</BBtn>
		</div>
	</SimulatorSheet>
</template>

<script>
import { mapMutations, mapActions, mapGetters, mapState } from "vuex";
import { deepClone } from "@/utils";

import SimulatorSheet from "./SimulatorSheet.vue";

import Draggable from "vuedraggable";

import BSelector from "@/components/base/BSelector.vue";
import BSelect from "@/components/base/BSelect.vue";
import BFieldBlock from "@/components/base/BFieldBlock.vue";
import BCheckbox from "@/components/base/BCheckbox.vue";
import BFieldNumber from "@/components/base/BFieldNumber.vue";
import BBtn from "@/components/base/BBtn.vue";
import BToggle from "@/components/base/BToggle.vue";
import BIcon from "@/components/base/BIcon/index.vue";

import ImageWind from "./Images/ImageWind.vue";

import mixinEmitLifecycleEvents from "@/mixins/emitLifecycleEvents";
import mixinObserveChanges from "@/pages/Simulator/mixins/observeChanges";
import { getDefaultsWind } from "../../store/modules/simulations/models/simulationDefaults";

export default {
	components: {
		BSelector,
		BToggle,
		BFieldBlock,
		BCheckbox,
		BSelect,
		BFieldNumber,
		BBtn,
		BIcon,
		SimulatorSheet,
		Draggable,
		ImageWind,
	},
	mixins: [mixinEmitLifecycleEvents, mixinObserveChanges],
	props: {
		...SimulatorSheet.props,
	},
	beforeRouteLeave(to, from, next) {
		next();
	},
	data() {
		return {
			values: null,
			isSaving: false,
		};
	},
	computed: {
		...mapState("simulator", {
			simulationOptions: "simulationOptions",
			simulationDefaults: "simulationDefaults",
			storeSimulation: "simulation",
		}),

		storeValues() {
			return this.storeSimulation.values;
		},
		...mapGetters("simulator", ["getMaxForLengthField"]),
	},
	methods: {
		...mapMutations("simulator", ["setSimulationValues"]),
		...mapActions("simulator", ["save"]),

		handleSaveBtnClick() {
			this.$emit("save", this.values);
		},

		handleRestartBtnClick() {
			this.$emit("restart");
		},

		handleAddLocalWindBtnClick() {
			this.values.wind.local.use = true;
		},

		handleDeleteLocalWindBtnClick() {
			this.values.wind.local.use = false;
		},

		handleRefreshLocalWindBtnClick() {
			const windDefaults = getDefaultsWind();

			this.values.wind.local = {
				...windDefaults.local,
				use: true,
			};
		},
	},

	watch: {
		"values.wind.local.use": {
			handler(use) {
				if (!use) {
					const windDefaults = getDefaultsWind();

					this.values.wind.local = {
						...windDefaults.local,
						use: false,
					};
				}
			},
		},
		...[
			"general.pressure",
			"general.suction",
			"local.pressure",
			"local.suction",
		].reduce((result, pathSubStr) => {
			return {
				...result,
				[`values.wind.${pathSubStr}`]: {
					handler(val) {
						const { isBandByBand } = this.values.wind.cfgMode;

						if (!isBandByBand) {
							val.w[1] = val.w[0];
							val.w[2] = val.w[0];
						}
					},
					deep: true,
				},
			};
		}, {}),
		"values.wind.cfgMode.isBandByBand": {
			handler(isBandByBand) {
				[
					this.values.wind.general.pressure,
					this.values.wind.general.suction,
					this.values.wind.local.pressure,
					this.values.wind.local.suction,
				].forEach((val) => {
					if (!isBandByBand) {
						val.w[1] = val.w[0];
						val.w[2] = val.w[0];
					}
				});
			},
		},

		"values.wind.cfgMode.deltaCfgOption": {
			handler(val) {
				this.values.wind.deltas = [
					...this.simulationOptions.windDeltasCfg.options.find(
						(option) => option.id === val
					).deltas,
				];
			},
		},
	},

	created() {
		this.values = deepClone(this.storeValues);
	},
};
</script>

<style lang="scss">
.localWindCard {
	border-radius: 8px;
	padding: 16px 16px 60px;
	background: var(--color-neutral-150);
}
</style>
